import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Zap, Check, Loader2, CreditCard, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const QUICK_PACKAGES = {
  "100": { credits: 100, price: 10, popular: false, bonus: 0 },
  "300": { credits: 300, price: 25, popular: true, bonus: 30 },
  "500": { credits: 500, price: 40, popular: false, bonus: 50 },
  "1000": { credits: 1000, price: 75, popular: false, bonus: 100 }
};

const QuickTopUpModal = ({ open, onOpenChange, currentCredits, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState("300");
  const { user, isAuthenticated } = useAuth();

  const handleTopUp = async (packageId) => {
    setLoading(true);
    setSelectedPackage(packageId);
    
    try {
      const originUrl = window.location.origin;
      
      // Check if user is authenticated
      if (!isAuthenticated || !user || !user.id) {
        toast.error('Please login to top up credits');
        setLoading(false);
        return;
      }
      
      // Use the same working endpoint as Pricing page
      const response = await axios.post(
        `${API_URL}/api/checkout/session`,
        {
          package_id: packageId,
          origin_url: originUrl,
          user_id: user.id
        }
      );
      
      if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Top-up error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate top-up');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-lg">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <DialogTitle className="text-xl">Quick Credit Top-Up</DialogTitle>
          </div>
          <DialogDescription>
            You currently have <span className="font-bold text-indigo-600">{currentCredits} credits</span>. 
            Top up instantly to continue analyzing!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          {Object.entries(QUICK_PACKAGES).map(([id, pkg]) => (
            <Card
              key={id}
              className={`p-4 cursor-pointer transition-all hover:shadow-lg ${
                selectedPackage === id ? 'border-2 border-indigo-600 bg-indigo-50' : 'border-2 border-transparent'
              } ${pkg.popular ? 'ring-2 ring-purple-400' : ''}`}
              onClick={() => setSelectedPackage(id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${pkg.popular ? 'bg-gradient-to-r from-purple-500 to-indigo-500' : 'bg-indigo-100'}`}>
                    <Sparkles className={`w-5 h-5 ${pkg.popular ? 'text-white' : 'text-indigo-600'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-lg text-gray-900">
                        {pkg.credits} Credits
                        {pkg.bonus > 0 && (
                          <span className="ml-2 text-xs font-normal text-green-600">
                            +{pkg.bonus} bonus
                          </span>
                        )}
                      </p>
                      {pkg.popular && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-full">
                          Most Popular
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700">${(pkg.price / pkg.credits).toFixed(3)} per credit</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-indigo-700">${pkg.price}</p>
                  <p className="text-xs text-gray-600">one-time</p>
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Check className="w-3 h-3 text-green-600" />
                  <span>Instant delivery</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Check className="w-3 h-3 text-green-600" />
                  <span>Never expires</span>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Maybe Later
          </Button>
          <Button
            className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            onClick={() => handleTopUp(selectedPackage)}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Top Up Now
              </>
            )}
          </Button>
        </div>

        <div className="mt-4 text-center">
          <a 
            href="/pricing" 
            className="text-sm text-indigo-600 hover:text-indigo-700 font-semibold underline"
            onClick={() => onOpenChange(false)}
          >
            💎 Subscribe to Save - Get recurring credits at a discount!
          </a>
        </div>

        <p className="text-xs text-center text-gray-500 mt-2">
          Secure payment powered by Stripe • Credits added instantly
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default QuickTopUpModal;
